import React, { useState } from 'react';
import { TentIcon, MapPinIcon, CalendarIcon, UsersIcon, InfoIcon, ArrowLeftIcon, SaveIcon, XIcon, GlobeIcon, ImageIcon, LinkIcon } from 'lucide-react';
export function FairForm({
  fair = null,
  onSubmit,
  onCancel
}) {
  const [formData, setFormData] = useState({
    id: fair?.id || null,
    name: fair?.name || '',
    location: fair?.location || '',
    startDate: fair?.startDate || '',
    endDate: fair?.endDate || '',
    attendees: fair?.attendees || '',
    description: fair?.description || '',
    website: fair?.website || '',
    contactPerson: fair?.contactPerson || '',
    contactEmail: fair?.contactEmail || '',
    contactPhone: fair?.contactPhone || '',
    logoUrl: fair?.logoUrl || '',
    status: fair?.status || 'upcoming'
  });
  const [errors, setErrors] = useState({});
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Fair name is required';
    if (!formData.location.trim()) newErrors.location = 'Location is required';
    if (!formData.startDate) newErrors.startDate = 'Start date is required';
    if (!formData.endDate) newErrors.endDate = 'End date is required';
    if (formData.startDate && formData.endDate && new Date(formData.startDate) > new Date(formData.endDate)) {
      newErrors.endDate = 'End date must be after start date';
    }
    if (!formData.attendees) {
      newErrors.attendees = 'Expected attendees is required';
    } else if (isNaN(formData.attendees) || Number(formData.attendees) <= 0) {
      newErrors.attendees = 'Attendees must be a positive number';
    }
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = e => {
    e.preventDefault();
    if (validate()) {
      onSubmit({
        ...formData,
        attendees: Number(formData.attendees)
      });
    }
  };
  return <div data-id="element-58" data-mp="3n9yxx0jkb65s">
      <div className="flex justify-between items-center mb-6" data-id="element-59" data-mp="3ndwlbyd6ajvh">
        <div className="flex items-center" data-id="element-60" data-mp="3ibnqsxknj9r9">
          <button onClick={onCancel} className="mr-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-blue-300 transition-colors" data-id="element-61" data-mp="3if34bvyogaqk">
            <ArrowLeftIcon size={20} data-id="element-62" data-mp="38mdzhjxmtv47" />
          </button>
          <div data-id="element-63" data-mp="38kt27mjxny2n">
            <h2 className="text-2xl font-bold text-white flex items-center" data-id="element-64" data-mp="34lbvkpsrsuvw">
              <TentIcon className="mr-2" size={24} data-id="element-65" data-mp="34j7uefm9a9zb" />
              {fair ? 'Edit Agricultural Fair' : 'Register New Agricultural Fair'}
            </h2>
            <p className="text-blue-300 mt-1" data-id="element-66" data-mp="2une8i5mnd44v">
              {fair ? 'Update the details of an existing fair' : 'Enter the details of the agricultural fair you will be attending'}
            </p>
          </div>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6" data-id="element-67" data-mp="2fjuywnyn138e">
        <div className="bg-white/5 backdrop-blur-sm border border-blue-800/30 rounded-xl p-6" data-id="element-68" data-mp="2kiekgn9bkd08">
          <h3 className="text-lg font-medium text-white mb-4" data-id="element-69" data-mp="2pi0yv00vvzam">
            Basic Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-70" data-mp="2ugc93bv3cbtd">
            <div data-id="element-71" data-mp="1wtmwy96ocits">
              <label htmlFor="name" className="block text-sm font-medium text-blue-300 mb-1" data-id="element-72" data-mp="1wu525vbbe7du">
                Fair Name <span className="text-red-400" data-id="element-73" data-mp="21rwxkzb64bms">*</span>
              </label>
              <div className="relative" data-id="element-74" data-mp="26s2ryn8toc8k">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-75" data-mp="26r15lmmcw3bj">
                  <TentIcon size={18} className="text-blue-400" data-id="element-76" data-mp="2bjazfdqldnj3" />
                </div>
                <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} className={`w-full bg-white/10 border ${errors.name ? 'border-red-500' : 'border-blue-800/30'} rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500`} placeholder="e.g. Agri Expo 2023" data-id="element-77" data-mp="1e22n2oebmz5p" />
              </div>
              {errors.name && <p className="mt-1 text-sm text-red-400" data-id="element-78" data-mp="3uhh1qryekvks">{errors.name}</p>}
            </div>
            <div data-id="element-79" data-mp="3ppz2c8c1331x">
              <label htmlFor="location" className="block text-sm font-medium text-blue-300 mb-1" data-id="element-80" data-mp="3ppfu5jc6jt13">
                Location <span className="text-red-400" data-id="element-81" data-mp="7b6kkvz9gy7w">*</span>
              </label>
              <div className="relative" data-id="element-82" data-mp="3bfqojefu1z5">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-83" data-mp="3amprz25u6ox">
                  <MapPinIcon size={18} className="text-blue-400" data-id="element-84" data-mp="h9w38qyzemp7" />
                </div>
                <input type="text" id="location" name="location" value={formData.location} onChange={handleChange} className={`w-full bg-white/10 border ${errors.location ? 'border-red-500' : 'border-blue-800/30'} rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500`} placeholder="e.g. Madrid, Spain" data-id="element-85" data-mp="cc41l58jbm7t" />
              </div>
              {errors.location && <p className="mt-1 text-sm text-red-400" data-id="element-86" data-mp="yj85mkelzzcl">{errors.location}</p>}
            </div>
            <div data-id="element-87" data-mp="jukb8hgi1vo8">
              <label htmlFor="startDate" className="block text-sm font-medium text-blue-300 mb-1" data-id="element-88" data-mp="jt0vqt2o3oni">
                Start Date <span className="text-red-400" data-id="element-89" data-mp="oru281jjefe6">*</span>
              </label>
              <div className="relative" data-id="element-90" data-mp="at436gd5q5n6">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-91" data-mp="ft8hbzqleoa8">
                  <CalendarIcon size={18} className="text-blue-400" data-id="element-92" data-mp="wr9hihm3rsf" />
                </div>
                <input type="date" id="startDate" name="startDate" value={formData.startDate} onChange={handleChange} className={`w-full bg-white/10 border ${errors.startDate ? 'border-red-500' : 'border-blue-800/30'} rounded-lg pl-10 pr-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500`} data-id="element-93" data-mp="5v2ijr031y9l" />
              </div>
              {errors.startDate && <p className="mt-1 text-sm text-red-400" data-id="element-94" data-mp="1ythkyu50bbvy">{errors.startDate}</p>}
            </div>
            <div data-id="element-95" data-mp="1triidmh3cj02">
              <label htmlFor="endDate" className="block text-sm font-medium text-blue-300 mb-1" data-id="element-96" data-mp="1tv6bwlmjd5es">
                End Date <span className="text-red-400" data-id="element-97" data-mp="1ovtnvxjxhsam">*</span>
              </label>
              <div className="relative" data-id="element-98" data-mp="1ku84dtcw2frg">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-99" data-mp="2hieegvsky0d4">
                  <CalendarIcon size={18} className="text-blue-400" data-id="element-100" data-mp="2cjup0les26g0" />
                </div>
                <input type="date" id="endDate" name="endDate" value={formData.endDate} onChange={handleChange} className={`w-full bg-white/10 border ${errors.endDate ? 'border-red-500' : 'border-blue-800/30'} rounded-lg pl-10 pr-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500`} data-id="element-101" data-mp="27smj3zcnp37l" />
              </div>
              {errors.endDate && <p className="mt-1 text-sm text-red-400" data-id="element-102" data-mp="2vhnm372pzm5h">{errors.endDate}</p>}
            </div>
            <div data-id="element-103" data-mp="i14btssq7hlp">
              <label htmlFor="attendees" className="block text-sm font-medium text-blue-300 mb-1" data-id="element-104" data-mp="i1nigdcxejkf">
                Expected Attendees <span className="text-red-400" data-id="element-105" data-mp="n0qp228k0xui">*</span>
              </label>
              <div className="relative" data-id="element-106" data-mp="qyxwg6h4omt1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-107" data-mp="vzc3pmawnsq0">
                  <UsersIcon size={18} className="text-blue-400" data-id="element-108" data-mp="10yx13safft0m" />
                </div>
                <input type="number" id="attendees" name="attendees" value={formData.attendees} onChange={handleChange} min="1" className={`w-full bg-white/10 border ${errors.attendees ? 'border-red-500' : 'border-blue-800/30'} rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500`} placeholder="e.g. 5000" data-id="element-109" data-mp="15xrufo60szyz" />
              </div>
              {errors.attendees && <p className="mt-1 text-sm text-red-400" data-id="element-110" data-mp="1onzsvsyrz28u">{errors.attendees}</p>}
            </div>
            <div data-id="element-111" data-mp="zku6c3z0pm4m">
              <label htmlFor="website" className="block text-sm font-medium text-blue-300 mb-1" data-id="element-112" data-mp="zkazpjetik5w">
                Fair Website
              </label>
              <div className="relative" data-id="element-113" data-mp="ulr8p4ldai6o">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-114" data-mp="18maioh9e7ybh">
                  <GlobeIcon size={18} className="text-blue-400" data-id="element-115" data-mp="14jno48lc7zal" />
                </div>
                <input type="url" id="website" name="website" value={formData.website} onChange={handleChange} className="w-full bg-white/10 border border-blue-800/30 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g. https://www.agriexpo2023.com" data-id="element-116" data-mp="gucqgzzaal0o" />
              </div>
            </div>
          </div>
          <div className="mt-6" data-id="element-117" data-mp="kud8svpi7gd0">
            <label htmlFor="description" className="block text-sm font-medium text-blue-300 mb-1" data-id="element-118" data-mp="3tb7x965k0y7k">
              Description <span className="text-red-400" data-id="element-119" data-mp="3p9d1siab1f1p">*</span>
            </label>
            <div className="relative" data-id="element-120" data-mp="3f5xw7r60ogek">
              <div className="absolute top-3 left-3 flex items-start pointer-events-none" data-id="element-121" data-mp="3f4wkrll39rwb">
                <InfoIcon size={18} className="text-blue-400" data-id="element-122" data-mp="3jxzf3vxox5dk" />
              </div>
              <textarea id="description" name="description" value={formData.description} onChange={handleChange} rows={4} className={`w-full bg-white/10 border ${errors.description ? 'border-red-500' : 'border-blue-800/30'} rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500`} placeholder="Describe the fair, its focus, and why we're participating..." data-id="element-123" data-mp="35931zkgs1263" />
            </div>
            {errors.description && <p className="mt-1 text-sm text-red-400" data-id="element-124" data-mp="3o15hutllgefa">{errors.description}</p>}
          </div>
        </div>
        <div className="bg-white/5 backdrop-blur-sm border border-blue-800/30 rounded-xl p-6" data-id="element-125" data-mp="2cjm96lp8vj9c">
          <h3 className="text-lg font-medium text-white mb-4" data-id="element-126" data-mp="2clyy5itf6tvw">
            Additional Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-127" data-mp="24mc8lfqaj3ta">
            <div data-id="element-128" data-mp="24o5h70kgry3x">
              <label htmlFor="contactPerson" className="block text-sm font-medium text-blue-300 mb-1" data-id="element-129" data-mp="29l5mvadeiyui">
                Contact Person
              </label>
              <input type="text" id="contactPerson" name="contactPerson" value={formData.contactPerson} onChange={handleChange} className="w-full bg-white/10 border border-blue-800/30 rounded-lg px-4 py-2.5 text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g. John Smith" data-id="element-130" data-mp="1uvhi1z60hi21" />
            </div>
            <div data-id="element-131" data-mp="1bxepxbi63500">
              <label htmlFor="contactEmail" className="block text-sm font-medium text-blue-300 mb-1" data-id="element-132" data-mp="1bxwv4xmt4tk2">
                Contact Email
              </label>
              <input type="email" id="contactEmail" name="contactEmail" value={formData.contactEmail} onChange={handleChange} className="w-full bg-white/10 border border-blue-800/30 rounded-lg px-4 py-2.5 text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g. contact@agriexpo.com" data-id="element-133" data-mp="1gwq90r60amkr" />
            </div>
            <div data-id="element-134" data-mp="uoktx7ix1my9">
              <label htmlFor="contactPhone" className="block text-sm font-medium text-blue-300 mb-1" data-id="element-135" data-mp="up40js348owz">
                Contact Phone
              </label>
              <input type="tel" id="contactPhone" name="contactPhone" value={formData.contactPhone} onChange={handleChange} className="w-full bg-white/10 border border-blue-800/30 rounded-lg px-4 py-2.5 text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g. +1 234 567 890" data-id="element-136" data-mp="14m8qm30314j2" />
            </div>
            <div data-id="element-137" data-mp="1sary4gbb5da3">
              <label htmlFor="logoUrl" className="block text-sm font-medium text-blue-300 mb-1" data-id="element-138" data-mp="1sbb6b5b5onax">
                Fair Logo URL
              </label>
              <div className="relative" data-id="element-139" data-mp="3u2xzndiisuak">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-140" data-mp="3u3ze7ryqvyx1">
                  <ImageIcon size={18} className="text-blue-400" data-id="element-141" data-mp="3p5o2fbow3ec6" />
                </div>
                <input type="url" id="logoUrl" name="logoUrl" value={formData.logoUrl} onChange={handleChange} className="w-full bg-white/10 border border-blue-800/30 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g. https://example.com/logo.png" data-id="element-142" data-mp="1t202t71xlo1b" />
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end space-x-4" data-id="element-143" data-mp="2byrmvipas76f">
          <button type="button" onClick={onCancel} className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors flex items-center" data-id="element-144" data-mp="2gyxh96myc7s7">
            <XIcon size={18} className="mr-2" data-id="element-145" data-mp="2lq5phzcjpn3s" />
            Cancel
          </button>
          <button type="submit" className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors shadow-md flex items-center" data-id="element-146" data-mp="2ppxqnqzm0qz4">
            <SaveIcon size={18} className="mr-2" data-id="element-147" data-mp="2zpevy7ay92uk" />
            {fair ? 'Update Fair' : 'Register Fair'}
          </button>
        </div>
      </form>
    </div>;
}